import { Container, Graphics, Sprite, Ticker } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { DefaultRandom, type Random } from '../core/Random';
import { Easings, tween, type TweenHandle } from '../core/Tween';

const DEFAULT_ICON_ALIAS = 'shared:icon:coin';

/**
 * "Coins fly to the balance" effect. Spawns N coin sprites from one or more
 * source points (typically every cell that contributed to the win — see
 * {@code sources} in {@link TakeWinAnimation#takeWin}), tweens each along a
 * slightly curved path with a stagger to a target point (the balance counter),
 * and fires {@code onCoinArrive(value)} as each one lands so the host can
 * increment the displayed balance smoothly rather than in one jump.
 *
 * <p>Coin count is capped (default 30) to keep the animation tractable on big
 * wins; each coin carries {@code ceil(winnings / coinCount)} value so the
 * sum reconciles. The final coin carries the remainder.
 *
 * <p>When multiple sources are passed, coins are distributed round-robin
 * across them — so a 30-coin take-win with 6 winning cells emits 5 coins
 * per cell, reading as "every winning symbol pays out". Single-source calls
 * still work (pass an array of one point).
 */
export class TakeWinAnimation extends Container {
  private active: { coin: Container; handle: TweenHandle }[] = [];

  constructor(
    private readonly ticker: Ticker,
    private readonly random: Random = new DefaultRandom(),
  ) {
    super();
  }

  /**
   * Animate {@code totalWinnings} flying as coins from {@code source} to
   * {@code target} (both in the same coordinate space as this container's
   * parent — typically stage-local pixels). {@code source} may be a single
   * point or an array of points; with an array, coins are distributed
   * round-robin so a multi-cell win has coins flying from each contributing
   * symbol. Resolves when the last coin lands.
   */
  takeWin(
    totalWinnings: number,
    source: { x: number; y: number } | ReadonlyArray<{ x: number; y: number }>,
    target: { x: number; y: number },
    onCoinArrive: (value: number) => void,
    options: TakeWinOptions = {},
  ): Promise<void> {
    if (totalWinnings <= 0) return Promise.resolve();

    const sources: ReadonlyArray<{ x: number; y: number }> = Array.isArray(source)
      ? (source.length > 0 ? source : [{ x: 0, y: 0 }])
      : [source as { x: number; y: number }];

    // When the caller knows the icon count up front (e.g. gem-on-reel,
    // where each gem is a discrete unit and the visual count should
    // match the awarded amount exactly), {@code iconCount} bypasses
    // the coin-style {@code max(8, ceil/50)} floor that otherwise
    // pads tiny wins out to a more substantial-looking burst.
    const coinCount = options.iconCount
      ?? Math.min(options.maxCoins ?? 30, Math.max(8, Math.ceil(totalWinnings / 50)));
    const valuePerCoin = Math.floor(totalWinnings / coinCount);
    const remainder = totalWinnings - valuePerCoin * coinCount;
    // Total duration = window from "first coin launches" to "last coin
    // lands". With simultaneous launch (producer audit Phase 4), the
    // per-coin flight time is randomised across {@code minFly..maxFly}
    // — every coin starts at t=0, but they arrive over the configured
    // window so the player still sees an incremental balance ticker.
    const totalDurationMs = options.totalDurationMs ?? 1200;
    const flyDurationMs = options.flyDurationMs ?? 700;
    // Per-coin flight randomisation spans [flyDurationMs, totalDurationMs]
    // — shortest coins arrive at the start, longest at the end.
    const minFly = flyDurationMs;
    const maxFly = Math.max(flyDurationMs, totalDurationMs);

    const iconAlias = options.iconAlias ?? DEFAULT_ICON_ALIAS;
    return new Promise<void>((resolve) => {
      let arrived = 0;
      for (let i = 0; i < coinCount; i++) {
        const valueForThis = i === coinCount - 1 ? valuePerCoin + remainder : valuePerCoin;
        // Round-robin source assignment — coin i comes from sources[i % N].
        const src = sources[i % sources.length]!;
        // Per-coin flight time. The first / last coins are clamped to
        // the min / max so the ticker spans the full window cleanly;
        // intermediate coins randomise within the range.
        let coinFlyMs: number;
        if (i === 0) {
          coinFlyMs = minFly;
        } else if (i === coinCount - 1) {
          coinFlyMs = maxFly;
        } else {
          coinFlyMs = minFly + this.random.nextFloat() * (maxFly - minFly);
        }
        // All coins launch simultaneously at t = 0 — no staggered
        // setTimeout. Spread arrival comes from the per-coin flight
        // variance + bezier-path randomisation inside spawnAndFly.
        this.spawnAndFly(src, target, coinFlyMs, iconAlias, () => {
          onCoinArrive(valueForThis);
          arrived++;
          if (arrived >= coinCount) {
            resolve();
          }
        });
      }
    });
  }

  /** Cancel any in-flight coins. */
  clear(): void {
    for (const entry of this.active) {
      entry.handle.cancel();
      entry.coin.destroy();
    }
    this.active = [];
  }

  destroyEffect(): void {
    this.clear();
  }

  private spawnAndFly(
    source: { x: number; y: number },
    target: { x: number; y: number },
    durationMs: number,
    iconAlias: string,
    onLand: () => void,
  ): void {
    const coin = this.makeCoin(iconAlias);
    coin.x = source.x;
    coin.y = source.y;
    this.addChild(coin);

    // Slight curve via a midpoint offset perpendicular to the source→target line.
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const dist = Math.hypot(dx, dy) || 1;
    const perpX = -dy / dist;
    const perpY = dx / dist;
    const curveStrength = 80 + this.random.nextFloat() * 80;
    const midX = (source.x + target.x) / 2 + perpX * curveStrength * (this.random.nextFloat() - 0.5) * 2;
    const midY = (source.y + target.y) / 2 + perpY * curveStrength * (this.random.nextFloat() - 0.5) * 2 - 30;

    const startScale = 0.7 + this.random.nextFloat() * 0.4;
    coin.scale.set(startScale);

    const handle = tween({
      durationMs,
      easing: Easings.easeInOutCubic,
      onUpdate: (p) => {
        // Quadratic Bezier through (source, mid, target).
        const oneMinusP = 1 - p;
        coin.x = oneMinusP * oneMinusP * source.x + 2 * oneMinusP * p * midX + p * p * target.x;
        coin.y = oneMinusP * oneMinusP * source.y + 2 * oneMinusP * p * midY + p * p * target.y;
        // Coin grows briefly mid-flight, then snaps small as it lands.
        const arc = Math.sin(p * Math.PI);
        coin.scale.set(startScale + arc * 0.4);
        coin.rotation = p * Math.PI * 4;
      },
      onComplete: () => {
        coin.destroy();
        // Drop from active list.
        this.active = this.active.filter((entry) => entry.coin !== coin);
        onLand();
      },
    });
    this.active.push({ coin, handle });
    void this.ticker; // ticker reserved for future use (sprite-sheet anim, etc.)
  }

  private makeCoin(iconAlias: string): Container {
    const c = new Container();
    // Prefer the commissioned texture for {@code iconAlias}; fall back
    // to a Graphics disc so the take-win still works in engine-only
    // test scenes that haven't loaded a theme. The alias defaults to
    // the coin glyph but callers can pass a different one (e.g.
    // 'shared:icon:gem' for the gem-on-reel flight to the wallet pill).
    //
    // Producer audit Phase 4: bumped target height 28 → 42 (1.5×)
    // so coins read clearly during flight against the busy slot art
    // + tier-treatment popups. Drawn-fallback radii bumped to match.
    const texture = AssetLoader.shared.getTexture(iconAlias);
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const targetH = 42;
      const aspect = texture.width / texture.height;
      sprite.height = targetH;
      sprite.width = targetH * aspect;
      c.addChild(sprite);
    } else {
      const g = new Graphics();
      g.circle(0, 0, 21)
        .fill({ color: 0xfff59d })
        .stroke({ color: 0xff8a3d, width: 2 });
      g.circle(0, 0, 14).stroke({ color: 0xff8a3d, width: 1.5 });
      c.addChild(g);
    }
    return c;
  }
}

export interface TakeWinOptions {
  maxCoins?: number;
  totalDurationMs?: number;
  flyDurationMs?: number;
  /** AssetLoader alias for the icon to fly. Defaults to
   *  {@code 'shared:icon:coin'} (the wallet coin glyph). Pass
   *  {@code 'shared:icon:gem'} for the gem-on-reel take-win so the
   *  flight reads as gems-into-wallet rather than coins-into-wallet. */
  iconAlias?: string;
  /** Exact number of icons to fly, bypassing the coin-style
   *  {@code max(8, ceil/50)} floor + {@code maxCoins} cap. Use when
   *  the caller knows the unit count up front and wants the visual
   *  to match exactly (e.g. gem-on-reel — landing 5 gems should fly
   *  5 icons, not 8). The 50-coin denomination math still runs so
   *  {@code valuePerCoin} is computed correctly. */
  iconCount?: number;
}
